import {
  AuthAccountContactTypeChoices,
  ContactType,
  FlagIcon,
  Typography,
  useFetchFile,
  UserTypeEnum,
} from "@cpj-ai/panel-npm-package";
import { AuthAccountProfileEmploymentStatusChoices, useGetJobSeekerByIdQuery } from "@graphql/generated";
import React from "react";
import { useParams } from "react-router-dom";

const JobSeekerPrintHeader: React.FC = () => {
  const { id } = useParams();

  const { data, loading } = useGetJobSeekerByIdQuery({
    variables: { id: id ?? "" },
  });

  const jobSeeker = data?.admin?.jobSeekers?.edges[0]?.node;
  const avatar = jobSeeker?.profile?.avatar?.file;
  const name = `${jobSeeker?.firstName ?? ""} ${jobSeeker?.lastName ?? ""}`.trim();
  const email = jobSeeker?.email;
  const country = jobSeeker?.profile?.city?.country?.name;
  const city = jobSeeker?.profile?.city?.name;
  const field = jobSeeker?.workexperiences?.[0]?.jobTitle;
  const company = jobSeeker?.workexperiences?.[0]?.organization;
  const contacts = jobSeeker?.profile?.contacts;
  const phone = contacts?.find((c: ContactType | null) => c?.type === AuthAccountContactTypeChoices.PHONE)?.value;
  const latestStatus = jobSeeker?.profile?.employmentStatus;

  const { data: avatarBlob } = useFetchFile({ urlPath: avatar, userType: UserTypeEnum.ADMIN });

  if (loading) {
    return (
      <div
        className="bg-white rounded-2xl border border-gray-100 shadow-sm print:shadow-none print:border-gray-300 overflow-hidden mb-2"
        style={{ breakAfter: "avoid-page", pageBreakAfter: "avoid" }}
      >
        <div className="w-full bg-gradient-to-r from-purple-50 via-white to-blue-50 border-b border-purple-100 px-5 py-4">
          <Typography className="text-xs font-semibold text-purple-700 mb-1">Jobseeker Overview</Typography>
        </div>
        <div className="px-5 py-4">
          <div className="flex items-center gap-4 animate-pulse">
            <div className="w-16 h-16 rounded-xl bg-gray-200 border border-gray-100" />
            <div className="flex-1 space-y-2">
              <div className="h-3.5 bg-gray-200 rounded w-1/3" />
              <div className="h-2.5 bg-gray-200 rounded w-1/2" />
              <div className="h-2.5 bg-gray-200 rounded w-1/4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 shadow-sm print:shadow-none print:border-gray-300 overflow-hidden mb-2"
      style={{
        breakAfter: "avoid-page",
        pageBreakAfter: "avoid",
      }}
    >
      {/* Gradient header, aligned with AssessmentDetailCard */}
      <div className="w-full bg-gradient-to-r from-purple-50 via-white to-blue-50 border-b border-purple-100 px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Typography className="text-xs font-semibold text-purple-700 mb-1">Jobseeker Overview</Typography>
          </div>

          {latestStatus && (
            <div className="flex items-start shrink-0 pe-2">
              {latestStatus === AuthAccountProfileEmploymentStatusChoices.EMPLOYED ? (
                <span className="px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap bg-green-50 text-green-700 border border-green-200">
                  Employed
                </span>
              ) : (
                <span className="px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap bg-red-50 text-red-700 border border-red-200">
                  {latestStatus.replace(/_/g, " ").toLowerCase()}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Body: avatar + location / role */}
      <div className="px-5 py-4">
        <div className="flex justify-between w-full items-center gap-4">
          {/* Avatar + identity */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 ">
              {avatarBlob ? (
                <img
                  src={avatarBlob}
                  alt="avatar"
                  width={64}
                  height={64}
                  className="rounded-xl object-cover border border-gray-100"
                />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-gray-200 border border-gray-100" />
              )}
            </div>
            <div>
              <Typography className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                {name || "Unnamed jobseeker"}
              </Typography>
              {(email || phone) && (
                <div className="flex flex-col gap-1">
                  {email && (
                    <Typography className="text-xs text-gray-600 mt-1 truncate">
                      {email}
                    </Typography>
                  )}
                  {phone && (
                    <Typography className="text-xs text-gray-600 mt-1 truncate">
                      {phone}
                    </Typography>
                  )}
                </div>
              )}
            </div>
          </div>
          {/* Location & role */}
          <div className="flex flex-col gap-1 sm:gap-2">
            {(city || country) && (
              <div className="flex items-center gap-1.5">
                <FlagIcon className="w-4 h-4 text-gray-500" />
                <Typography className="text-xs sm:text-sm text-gray-700">
                  {city || "City"}, {country || "Country"}
                </Typography>
              </div>
            )}
            {field && (
              <Typography className="text-xs sm:text-sm text-gray-700">
                {field}
                {company ? ` @ ${company}` : ""}
              </Typography>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobSeekerPrintHeader;

